/* eslint-disable no-console */
import { useState, useEffect, useMemo, useCallback } from "react";
import { FaTrash, FaUpload, FaCheckCircle } from "react-icons/fa";

import { USERS_ALL_VENTAS } from "../../constants/permisos";
import { ROLES } from "../../constants/roles";
import { useAuthContext } from "../../context/auth";
import {
  getOportunidades,
  getEdificiosFull,
  getMediaByClave,
  getReportesByClaves,
  deleteFotoReporte,
  getClientes,
  getContratos,
  construirUnidadesReportables,
  agruparReportesPorUnidad,
} from "../../utils";
import { Header } from "../Header";
import EmbudoVentaSelector, { periodoLabel, clavesEmbudo } from "./EmbudoVentaSelector";
import SubirFotoReporteModal from "./SubirFotoReporteModal";

const ROLES_EDITOR = [ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.COMERCIAL, ROLES.ADMINISTRATIVO];

// Karen (306) y Nicolás (904) acceden a todas las ventas para crear reportes
// USERS_ALL_VENTAS importado de permisos.js

const ReporteFotografico = () => {
  const { pais, id: userId, rol } = useAuthContext();
  const canEdit = ROLES_EDITOR.includes(rol);

  // Unidades reportables: ventas directas + cuotas de contrato, en un carril único.
  const [unidades, setUnidades] = useState([]);
  const [edificiosMap, setEdificiosMap] = useState({});
  const [clientesById, setClientesById] = useState({});
  // Fuente de verdad ÚNICA del selector: { empresa, productoKey, saleId }. Los tres
  // dropdowns del embudo y el detalle/grilla derivan de acá — imposible desincronizar (S4).
  const [selection, setSelection] = useState({ empresa: "", productoKey: "", saleId: "" });
  const selectedUnidadId = selection.saleId;
  const [fotos, setFotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingFotos, setLoadingFotos] = useState(false);

  // Reportes iniciados: Map<unidadId, { ultimaFoto, edificiosConFoto:Set, total }>.
  // Fuente única de "qué unidades tienen reporte" + su cronología/progreso.
  const [reportesResumen, setReportesResumen] = useState(new Map());
  // Orden de la tabla "Reportes iniciados". key=null → orden por defecto (última foto desc).
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "desc" });

  // Modal subir foto
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadEdificio, setUploadEdificio] = useState(null);

  // Cargar unidades (ventas + cuotas de contrato) y edificios
  useEffect(() => {
    if (!pais || pais === "Region") return;
    const currentYear = new Date().getFullYear();

    const loadData = async () => {
      try {
        const [salesData, edData, clientesData, contratosData] = await Promise.all([
          getOportunidades({ pais, ano: currentYear }).catch(() => []),
          getEdificiosFull(),
          getClientes({ pais }).catch(() => []),
          getContratos({ pais }).catch(() => []),
        ]);

        const allSales = salesData || [];

        // Mapa de clientes por id para resolver empresa/producto (regla 10.7: por id).
        const clientesMap = (clientesData || []).reduce((acc, c) => ({ ...acc, [c.id]: c }), {});
        setClientesById(clientesMap);

        const esComercialScoped = rol === ROLES.COMERCIAL && !USERS_ALL_VENTAS.includes(userId);

        // Ventas: cerradas (100%) + con edificios asignados (gates sin cambios).
        const ventasFiltradas = allSales
          .filter((s) => s.estatus === "100%" && s.edificiosparticipantes && s.edificiosparticipantes.trim() !== "")
          .sort((a, b) => (b.id || 0) - (a.id || 0));
        const ventasFinales = esComercialScoped
          ? ventasFiltradas.filter((s) => String(s.ejecutivo) === String(userId))
          : ventasFiltradas;

        // Contratos: gates espejo (100%, no eliminada, ejecutivo). País ya filtrado por getContratos.
        const contratosFiltrados = (contratosData || []).filter((c) => c.estatus === "100%" && c.eliminada !== true);
        const contratosFinales = esComercialScoped
          ? contratosFiltrados.filter((c) => String(c.ejecutivo) === String(userId))
          : contratosFiltrados;

        // Carril único. Gates de cuota (año del periodo + edificios asignados) van en el helper.
        setUnidades(construirUnidadesReportables(ventasFinales, contratosFinales, clientesMap, { ano: currentYear }));

        if (edData?.length > 0) {
          setEdificiosMap(edData.reduce((acc, e) => ({ ...acc, [e.id]: e }), {}));
        }
      } catch (error) {
        console.error("Error cargando datos:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [pais, rol, userId]);

  // Cargar qué unidades ya tienen reportes iniciados + su cronología/progreso.
  useEffect(() => {
    if (unidades.length === 0) return;
    const loadReportes = async () => {
      try {
        const filas = await getReportesByClaves(unidades.map((u) => u.clave));
        setReportesResumen(agruparReportesPorUnidad(filas));
      } catch (error) {
        console.error("Error cargando reportes iniciados:", error);
      }
    };
    loadReportes();
  }, [unidades]);

  // Ids de unidades con reporte (para el ✓/📷 del embudo y el conteo del botón).
  const unidadesConReporte = useMemo(() => [...reportesResumen.keys()], [reportesResumen]);

  // Lista de "reportes iniciados" ordenada por última foto subida (desc). Progreso
  // X/Y = edificios con >=1 archivo / total de edificios de la unidad (fuenteEdificios).
  const reportesLista = useMemo(() => {
    const filas = [];
    reportesResumen.forEach((g, uid) => {
      const unidad = unidades.find((u) => u.id === uid);
      if (!unidad) return;
      const totalEdificios = (unidad.fuenteEdificios || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean).length;
      filas.push({ unidad, ultimaFoto: g.ultimaFoto, edificiosConFoto: g.edificiosConFoto.size, totalEdificios });
    });
    filas.sort((a, b) => (b.ultimaFoto || "").localeCompare(a.ultimaFoto || ""));
    return filas;
  }, [reportesResumen, unidades]);

  // Click en header: primer click asc, segundo click alterna a desc.
  const handleSort = useCallback((key) => {
    setSortConfig((prev) => ({ key, direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc" }));
  }, []);

  // Lista ordenada según sortConfig. key=null → orden por defecto (última foto desc, de reportesLista).
  const reportesOrdenados = useMemo(() => {
    if (!sortConfig.key) return reportesLista;
    const dir = sortConfig.direction === "asc" ? 1 : -1;
    const alpha = (x) => (x || "").toString();
    // "MM-YYYY" → entero comparable cronológicamente (NO texto).
    const periodoNum = (mf) => {
      const m = (mf || "").match(/^(\d{1,2})-(\d{4})$/);
      return m ? parseInt(m[2], 10) * 12 + parseInt(m[1], 10) : 0;
    };
    const ratio = (r) => (r.totalEdificios > 0 ? r.edificiosConFoto / r.totalEdificios : 0);
    const cmp = {
      empresa: (a, b) => alpha(a.unidad.empresa).localeCompare(alpha(b.unidad.empresa), "es"),
      producto: (a, b) => alpha(a.unidad.producto).localeCompare(alpha(b.unidad.producto), "es"),
      campana: (a, b) => alpha(a.unidad.campana).localeCompare(alpha(b.unidad.campana), "es"),
      periodo: (a, b) => periodoNum(a.unidad.mes_factura) - periodoNum(b.unidad.mes_factura),
      edificios: (a, b) => a.totalEdificios - b.totalEdificios,
      reportados: (a, b) => ratio(a) - ratio(b),
    }[sortConfig.key];
    if (!cmp) return reportesLista;
    return [...reportesLista].sort((a, b) => dir * cmp(a, b));
  }, [reportesLista, sortConfig]);

  const sortArrow = (key) => (sortConfig.key === key ? (sortConfig.direction === "asc" ? " ▲" : " ▼") : "");

  const selectedUnidad = useMemo(() => unidades.find((u) => u.id === selectedUnidadId), [unidades, selectedUnidadId]);

  // Handlers del embudo (controlado): empresa/producto resetean el consumo aguas abajo.
  const handleEmpresa = useCallback((empresa) => setSelection({ empresa, productoKey: "", saleId: "" }), []);
  const handleProducto = useCallback((productoKey) => setSelection((s) => ({ ...s, productoKey, saleId: "" })), []);
  const handleSelectSale = useCallback((saleId) => setSelection((s) => ({ ...s, saleId })), []);

  // Seleccionar una unidad por CUALQUIER vía (chip): setea la selección completa para
  // que los tres dropdowns reflejen la unidad (misma fuente única).
  const seleccionarUnidad = useCallback(
    (unidad) => setSelection({ ...clavesEmbudo(unidad, clientesById), saleId: unidad.id }),
    [clientesById]
  );

  // Deselecciona y vuelve el embudo a vacío en un solo lugar.
  const limpiarSeleccion = useCallback(() => setSelection({ empresa: "", productoKey: "", saleId: "" }), []);

  // Cargar media cuando se selecciona una unidad
  useEffect(() => {
    if (!selectedUnidad) {
      setFotos([]);
      return;
    }
    const loadFotos = async () => {
      setLoadingFotos(true);
      try {
        const data = await getMediaByClave(selectedUnidad.clave);
        setFotos(data);
      } catch (error) {
        console.error("Error cargando fotos:", error);
      } finally {
        setLoadingFotos(false);
      }
    };
    loadFotos();
  }, [selectedUnidad]);

  // Edificios de la unidad seleccionada (venta: edificiosparticipantes; cuota: meses[].edificios_ids).
  const edificiosUnidad = useMemo(() => {
    if (!selectedUnidad?.fuenteEdificios) return [];
    const ids = selectedUnidad.fuenteEdificios
      .split(",")
      .map((s) => Number(s.trim()))
      .filter(Boolean);
    return ids
      .map((id) => edificiosMap[id])
      .filter(Boolean)
      .sort((a, b) => (a.edificio || "").localeCompare(b.edificio || ""));
  }, [selectedUnidad, edificiosMap]);

  // Fotos agrupadas por edificio
  const fotosPorEdificio = useMemo(() => {
    const map = {};
    fotos.forEach((f) => {
      if (!map[f.idedificio]) map[f.idedificio] = [];
      map[f.idedificio].push(f);
    });
    return map;
  }, [fotos]);

  const edificiosCompletos = useMemo(
    () => edificiosUnidad.filter((e) => fotosPorEdificio[e.id]?.length > 0).length,
    [edificiosUnidad, fotosPorEdificio]
  );

  const totalFotos = fotos.length;

  const refreshFotos = useCallback(async () => {
    if (!selectedUnidad) return;
    try {
      const data = await getMediaByClave(selectedUnidad.clave);
      setFotos(data);
      // Mantener el resumen (cronología/progreso) fresco tras subir/borrar en esta unidad.
      const uid = selectedUnidad.id;
      setReportesResumen((prev) => {
        const next = new Map(prev);
        const g = agruparReportesPorUnidad(data).get(uid);
        if (g) next.set(uid, g);
        else next.delete(uid);
        return next;
      });
    } catch (error) {
      console.error("Error recargando fotos:", error);
    }
  }, [selectedUnidad]);

  const handleDeleteMedia = async (record) => {
    if (!window.confirm("¿Eliminar este archivo del reporte?")) return;
    try {
      await deleteFotoReporte(record);
      await refreshFotos();
    } catch (error) {
      console.error("Error eliminando archivo:", error);
    }
  };

  const handleOpenUpload = (edificio) => {
    setUploadEdificio(edificio);
    setShowUploadModal(true);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div style={{ padding: 15 }}>
      <Header />

      <div className="container-fluid px-4 py-3">
        {/* Selector: embudo Empresa → Producto → Consumo (ventas + cuotas de contrato).
            Las unidades con reporte iniciado quedan en el Consumo con 📷 (no se ocultan). */}
        <div className="mb-4">
          <EmbudoVentaSelector
            ventas={unidades}
            clientesById={clientesById}
            selectedEmpresa={selection.empresa}
            selectedProductoKey={selection.productoKey}
            selectedSaleId={selectedUnidadId}
            onEmpresaChange={handleEmpresa}
            onProductoChange={handleProducto}
            onSelectSale={handleSelectSale}
            conReporteIds={unidadesConReporte}
          />
          {selectedUnidadId && (
            <button className="btn btn-sm btn-outline-secondary mt-2" onClick={limpiarSeleccion}>
              Limpiar selecci&oacute;n
            </button>
          )}
        </div>

        {/* Reportes iniciados: lista cronológica directa (última foto primero) */}
        {!selectedUnidadId && unidadesConReporte.length > 0 && (
          <div className="mb-4">
            <label className="form-label fw-semibold text-muted" style={{ fontSize: "0.85rem" }}>
              Reportes iniciados ({unidadesConReporte.length})
            </label>
            <div className="table-responsive">
              <table
                className="table table-bordered table-striped table-hover align-middle mb-0"
                style={{ fontSize: "0.9rem" }}
              >
                <thead className="table-dark">
                  <tr style={{ cursor: "pointer", userSelect: "none" }}>
                    <th className="text-start" style={{ width: "20%" }} onClick={() => handleSort("empresa")}>
                      Empresa{sortArrow("empresa")}
                    </th>
                    <th className="text-start" style={{ width: "18%" }} onClick={() => handleSort("producto")}>
                      Producto{sortArrow("producto")}
                    </th>
                    <th className="text-start" style={{ width: "26%" }} onClick={() => handleSort("campana")}>
                      Campa&ntilde;a{sortArrow("campana")}
                    </th>
                    <th className="text-center" style={{ width: "12%" }} onClick={() => handleSort("periodo")}>
                      Per&iacute;odo{sortArrow("periodo")}
                    </th>
                    <th className="text-center" style={{ width: "10%" }} onClick={() => handleSort("edificios")}>
                      Edificios{sortArrow("edificios")}
                    </th>
                    <th className="text-center" style={{ width: "14%" }} onClick={() => handleSort("reportados")}>
                      Reportados{sortArrow("reportados")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {reportesOrdenados.map(({ unidad, edificiosConFoto, totalEdificios }) => {
                    const pct = totalEdificios > 0 ? Math.round((edificiosConFoto / totalEdificios) * 100) : 0;
                    return (
                      <tr
                        key={unidad.id}
                        style={{ cursor: "pointer" }}
                        title="Abrir reporte"
                        onClick={() => seleccionarUnidad(unidad)}
                      >
                        <td className="text-start">{unidad.empresa}</td>
                        <td className="text-start">{unidad.producto || "—"}</td>
                        <td className="text-start">{unidad.campana || "—"}</td>
                        <td className="text-center">{periodoLabel(unidad.mes_factura) || "—"}</td>
                        <td className="text-center">{totalEdificios}</td>
                        <td className="text-center">
                          {edificiosConFoto} ({pct}%)
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Resumen de la unidad (venta o cuota de contrato) */}
        {selectedUnidad && (
          <>
            <div className="row mb-3">
              <div className="col-12">
                <div className="p-3 bg-light rounded border">
                  <div className="row">
                    <div className="col-md-3">
                      <small className="text-muted">Cliente</small>
                      <div className="fw-semibold">{selectedUnidad.cliente}</div>
                    </div>
                    {selectedUnidad.campana && (
                      <div className="col-md-3">
                        <small className="text-muted">Campa&ntilde;a</small>
                        <div className="fw-semibold">{selectedUnidad.campana}</div>
                      </div>
                    )}
                    {selectedUnidad.intermediario && (
                      <div className="col-md-2">
                        <small className="text-muted">Agencia</small>
                        <div className="fw-semibold">{selectedUnidad.intermediario}</div>
                      </div>
                    )}
                    <div className="col-md-2">
                      <small className="text-muted">Edificios</small>
                      <div className="fw-semibold">{selectedUnidad.edificiostotales}</div>
                    </div>
                    {selectedUnidad.periodopauta && (
                      <div className="col-md-2">
                        <small className="text-muted">Per&iacute;odo</small>
                        <div className="fw-semibold">{selectedUnidad.periodopauta} d&iacute;as</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Barra de progreso */}
            <div className="row mb-3">
              <div className="col-12">
                <div className="d-flex align-items-center gap-3">
                  <span className="fw-semibold" style={{ fontSize: "0.9rem" }}>
                    {edificiosCompletos} de {edificiosUnidad.length} edificios con contenido
                  </span>
                  <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                    ({totalFotos} {totalFotos === 1 ? "archivo" : "archivos"} en total)
                  </span>
                  <div className="flex-grow-1">
                    <div className="progress" style={{ height: "8px" }}>
                      <div
                        className="progress-bar bg-success"
                        style={{
                          width:
                            edificiosUnidad.length > 0
                              ? `${(edificiosCompletos / edificiosUnidad.length) * 100}%`
                              : "0%",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Grilla compacta de edificios */}
            {loadingFotos ? (
              <div className="text-center py-4">
                <div className="spinner-border spinner-border-sm text-primary" role="status" />
                <span className="ms-2">Cargando fotos...</span>
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                  gap: 12,
                  padding: "16px 24px",
                }}
              >
                {edificiosUnidad.map((edificio) => {
                  const mediaEdificio = fotosPorEdificio[edificio.id] || [];
                  const foto = mediaEdificio.find((m) => m.tipo === "foto") || null;
                  const video = mediaEdificio.find((m) => m.tipo === "video") || null;
                  const completo = mediaEdificio.length > 0;
                  const tieneSlotLibre = !foto || !video;
                  const itemHeight = foto && video ? 80 : 120;

                  const deleteBtnStyle = {
                    top: 4,
                    right: 4,
                    padding: "2px 4px",
                    fontSize: "0.55rem",
                    background: "rgba(220,53,69,0.85)",
                    color: "#fff",
                    borderRadius: "50%",
                    border: "none",
                    lineHeight: 1,
                  };

                  return (
                    <div
                      key={edificio.id}
                      style={{
                        border: completo ? "1.5px solid #198754" : "1px solid #dee2e6",
                        borderRadius: 6,
                        background: completo ? "#f0fdf4" : "#fff",
                        cursor: canEdit && tieneSlotLibre ? "pointer" : "default",
                        fontSize: "0.7rem",
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                      }}
                      onClick={() => {
                        if (canEdit && tieneSlotLibre) handleOpenUpload(edificio);
                      }}
                      title={edificio.direccion || edificio.ubicacion || edificio.edificio}
                    >
                      {/* Título y dirección arriba, ancho completo */}
                      <div style={{ padding: "6px 8px", lineHeight: 1.3 }}>
                        <div className="d-flex align-items-center gap-1" style={{ marginBottom: 2 }}>
                          <span
                            className="fw-semibold"
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: "0.72rem",
                            }}
                          >
                            {edificio.edificio}
                          </span>
                          {completo && (
                            <FaCheckCircle className="text-success" style={{ fontSize: "0.55rem", flexShrink: 0 }} />
                          )}
                        </div>
                        {edificio.direccion && (
                          <div
                            className="text-muted"
                            style={{
                              fontSize: "0.6rem",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {edificio.direccion}
                          </div>
                        )}
                      </div>

                      {/* Foto */}
                      {foto && (
                        <div style={{ width: "100%", height: itemHeight, position: "relative" }}>
                          <img
                            src={foto.url}
                            alt=""
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              cursor: "pointer",
                              borderRadius: video ? 0 : "0 0 5px 5px",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(foto.url, "_blank");
                            }}
                          />
                          {canEdit && (
                            <button
                              className="btn btn-sm position-absolute"
                              style={deleteBtnStyle}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteMedia(foto);
                              }}
                              title="Eliminar foto"
                            >
                              <FaTrash style={{ fontSize: "0.5rem" }} />
                            </button>
                          )}
                        </div>
                      )}

                      {/* Video */}
                      {video && (
                        <div style={{ width: "100%", height: itemHeight, position: "relative" }}>
                          <video
                            src={video.url}
                            autoPlay
                            muted
                            loop
                            playsInline
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              cursor: "pointer",
                              borderRadius: "0 0 5px 5px",
                              display: "block",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(video.url, "_blank");
                            }}
                          />
                          {canEdit && (
                            <button
                              className="btn btn-sm position-absolute"
                              style={deleteBtnStyle}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteMedia(video);
                              }}
                              title="Eliminar video"
                            >
                              <FaTrash style={{ fontSize: "0.5rem" }} />
                            </button>
                          )}
                        </div>
                      )}

                      {/* Ícono subir — grande si vacío, chico si falta un slot */}
                      {canEdit && tieneSlotLibre && (
                        <div
                          className="text-muted d-flex align-items-center justify-content-center"
                          style={{ height: mediaEdificio.length > 0 ? 24 : 40 }}
                        >
                          <FaUpload
                            style={{
                              fontSize: mediaEdificio.length > 0 ? "0.65rem" : "0.9rem",
                              opacity: 0.3,
                            }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {canEdit && (
        <SubirFotoReporteModal
          show={showUploadModal}
          onHide={() => {
            setShowUploadModal(false);
            setUploadEdificio(null);
          }}
          onUploadSuccess={() => {
            setShowUploadModal(false);
            setUploadEdificio(null);
            refreshFotos();
          }}
          onDeleteMedia={handleDeleteMedia}
          edificio={uploadEdificio}
          clave={selectedUnidad?.clave || null}
          mediaActual={(uploadEdificio && fotosPorEdificio[uploadEdificio.id]) || []}
          userId={userId}
          canEdit={canEdit}
        />
      )}
    </div>
  );
};

export default ReporteFotografico;
